import React, { useEffect, useMemo, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  Bot,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Circle,
  Download,
  FileCheck2,
  FileText,
  Files,
  Gavel,
  Loader2,
  Paperclip,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { getApiBaseUrl, getAuthHeaders } from '../config/api';
import { Tender } from '../types';
import { useModal } from './ModalProvider';
import AuctionRoom from './AuctionRoom';

type ParticipationAction = 'SUMMARY' | 'DETAILED' | 'CHECKLIST' | 'DECLARATION' | 'PROPOSAL';
type ParticipationTab = 'FILES' | 'ANALYSIS' | 'AUCTION';

const ACTIONS: Array<{ key: ParticipationAction; label: string; group: 'ANALYSIS' | 'PREPARATION'; prompt: string }> = [
  { key: 'SUMMARY', label: 'Análise resumida', group: 'ANALYSIS', prompt: 'Gere uma análise executiva resumida do edital: objeto, órgão, prazo, valor, modalidade, riscos, próximos passos e decisão preliminar de participação. Sinalize explicitamente qualquer dado ausente.' },
  { key: 'DETAILED', label: 'Análise criteriosa', group: 'ANALYSIS', prompt: 'Atue como especialista em licitações. Faça uma análise criteriosa do edital: requisitos técnicos, habilitação jurídica/fiscal/técnica/econômica, critérios de julgamento, prazos, obrigações, penalidades, riscos, pontos de impugnação/esclarecimento e recomendação justificada. Não invente requisitos ausentes.' },
  { key: 'CHECKLIST', label: 'Checklist documental', group: 'PREPARATION', prompt: 'Crie um checklist documental organizado por: habilitação jurídica, regularidade fiscal/trabalhista, qualificação técnica, qualificação econômico-financeira, proposta comercial, declarações e documentos complementares. Para cada item, informe evidência no edital, responsável sugerido e prazo. Não inclua documentos não exigidos sem marcar como recomendação.' },
  { key: 'DECLARATION', label: 'Minutas de declarações', group: 'PREPARATION', prompt: 'Elabore minutas editáveis das declarações exigidas no edital. Use campos [PREENCHER] para dados da empresa, representante, processo e datas. Separe cada declaração com título. Não assine nem afirme fatos que não estejam comprovados.' },
  { key: 'PROPOSAL', label: 'Estrutura de proposta', group: 'PREPARATION', prompt: 'Crie a estrutura de uma proposta comercial aderente ao edital: capa, identificação, objeto, solução técnica, itens/quantidades, preços a preencher, prazo de validade, condições de entrega/execução, garantia e anexos. Use [PREENCHER] onde faltarem dados.' },
];

const TABS: Array<{ key: ParticipationTab; label: string; mobileLabel: string; icon: typeof Files }> = [
  { key: 'FILES', label: 'Arquivos', mobileLabel: 'Arquivos', icon: Files },
  { key: 'ANALYSIS', label: 'Análise e minutas', mobileLabel: 'Análise', icon: Bot },
  { key: 'AUCTION', label: 'Sala de pregão', mobileLabel: 'Pregão', icon: Gavel },
];

const PRIORITY_LABELS: Record<string, string> = {
  NONE: 'Sem prioridade',
  PRIORITY: 'Prioridade',
  VERY_HIGH: 'Altíssima prioridade',
  ULTRA: 'Ultra prioridade',
  NO_GO: 'Não participar',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Não informado';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const formatMoney = (value?: number) => Number(value || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.readAsDataURL(file);
});

const Participation: React.FC = () => {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [selectedTenderId, setSelectedTenderId] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState('');
  const [analysisTitle, setAnalysisTitle] = useState('Análise de participação');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [activeAction, setActiveAction] = useState<ParticipationAction | null>(null);
  const [activeTab, setActiveTab] = useState<ParticipationTab>('FILES');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showAlert } = useModal();

  const selectedTender = useMemo(
    () => tenders.find((tender) => tender.id === selectedTenderId) || null,
    [tenders, selectedTenderId],
  );

  const preparationItems = useMemo(() => selectedTender ? [
    { label: 'Separados', ready: Boolean(selectedTender.documentsSeparated) },
    { label: 'Validados', ready: Boolean(selectedTender.documentsValidated) },
    { label: 'Marcas', ready: Boolean(selectedTender.brandsValidated) },
    { label: 'Modelos', ready: Boolean(selectedTender.modelsValidated) },
    { label: 'Valores', ready: Boolean(selectedTender.valuesValidated) },
  ] : [], [selectedTender]);

  const preparationReady = preparationItems.filter((item) => item.ready).length;

  const loadAttachments = async (tenderId: string) => {
    if (!tenderId) return setAttachments([]);
    try {
      setAttachments(await apiService.getTenderAttachments(tenderId));
    } catch (error) {
      console.error('Erro ao carregar anexos da participação:', error);
      setAttachments([]);
    }
  };

  useEffect(() => {
    apiService.getTenders()
      .then((items) => {
        const active = items.filter((item) => ['PARTICIPATION', 'AUCTION'].includes(String(item.workflowStage)));
        setTenders(active);
        const hashQuery = window.location.hash.split('?')[1] || '';
        const params = new URLSearchParams(hashQuery);
        const requestedTenderId = params.get('opportunity');
        const requestedTab = params.get('tab');
        const requestedTender = active.find((item) => item.id === requestedTenderId);
        if (requestedTender) setSelectedTenderId(requestedTender.id);
        else if (active[0]) setSelectedTenderId(active[0].id);
        if (requestedTab === 'AUCTION') setActiveTab('AUCTION');
      })
      .catch((error) => console.error('Erro ao carregar licitações:', error))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const syncHashSelection = () => {
      const hashQuery = window.location.hash.split('?')[1] || '';
      const params = new URLSearchParams(hashQuery);
      const requestedTenderId = params.get('opportunity');
      const requestedTab = params.get('tab');
      if (requestedTenderId && tenders.some((item) => item.id === requestedTenderId)) {
        setSelectedTenderId(requestedTenderId);
      }
      if (requestedTab === 'AUCTION') setActiveTab('AUCTION');
      if (requestedTab === 'FILES') setActiveTab('FILES');
      if (requestedTab === 'ANALYSIS') setActiveTab('ANALYSIS');
    };

    syncHashSelection();
    window.addEventListener('hashchange', syncHashSelection);
    return () => window.removeEventListener('hashchange', syncHashSelection);
  }, [tenders]);

  useEffect(() => {
    setSelectedPdf(null);
    setActiveAction(null);
    loadAttachments(selectedTenderId);
  }, [selectedTenderId]);

  useEffect(() => {
    if (!selectedTenderId) return;
    try {
      const saved = localStorage.getItem(`participation-analysis:${selectedTenderId}`);
      const parsed = saved ? JSON.parse(saved) : null;
      setAnalysis(parsed?.analysis || '');
      setAnalysisTitle(parsed?.title || 'Análise de participação');
    } catch {
      setAnalysis('');
      setAnalysisTitle('Análise de participação');
    }
  }, [selectedTenderId]);

  useEffect(() => {
    if (!selectedTenderId || !analysis) return;
    localStorage.setItem(`participation-analysis:${selectedTenderId}`, JSON.stringify({ title: analysisTitle, analysis }));
  }, [analysis, analysisTitle, selectedTenderId]);

  const uploadAttachment = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTenderId) return;
    try {
      setWorking(true);
      const base64 = await toBase64(file);
      const response = await fetch(`${getApiBaseUrl()}/tenders/${selectedTenderId}/attachments/upload-base64`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: file.name, type: file.type, base64 }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Não foi possível enviar o anexo.');
      if (file.type === 'application/pdf') setSelectedPdf(file);
      await loadAttachments(selectedTenderId);
      await showAlert('Anexo adicionado à participação.', 'Sucesso');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao enviar anexo.', 'Erro');
    } finally {
      setWorking(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openAttachment = async (attachment: { url?: string; name?: string }) => {
    if (!attachment.url) return;
    if (/^https?:\/\//i.test(attachment.url)) {
      window.open(attachment.url, '_blank', 'noopener,noreferrer');
      return;
    }

    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      await showAlert('O navegador bloqueou a nova guia. Permita pop-ups para abrir o arquivo.', 'Atenção');
      return;
    }
    popup.opener = null;

    try {
      const encodedKey = attachment.url.split('/').map(encodeURIComponent).join('/');
      const response = await fetch(`${getApiBaseUrl()}/uploads/${encodedKey}/view`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Não foi possível abrir o arquivo.');
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      popup.location.replace(objectUrl);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      popup.close();
      await showAlert(error instanceof Error ? error.message : 'Não foi possível abrir o arquivo.', 'Erro');
    }
  };

  const runAction = async (action: typeof ACTIONS[number]) => {
    if (!selectedTender) return;
    if (!selectedPdf) {
      await showAlert('Envie e selecione um PDF do edital para gerar a análise.', 'Arquivo necessário');
      return;
    }
    try {
      setWorking(true);
      setActiveAction(action.key);
      const base64 = await toBase64(selectedPdf);
      const context = `Licitação: ${selectedTender.title}\nÓrgão: ${selectedTender.organ}\nPrazo: ${selectedTender.deadline}\nDescrição cadastrada: ${selectedTender.description || 'Não informada'}\n\n${action.prompt}`;
      const result = await apiService.analyzeTenderDocument(base64, context, []);
      setAnalysis(result.text || 'A IA não retornou conteúdo.');
      setAnalysisTitle(`${action.label} - ${selectedTender.title}`);
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao gerar conteúdo com IA.', 'Erro');
    } finally {
      setWorking(false);
    }
  };

  const exportPdf = () => {
    if (!analysis) return;
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 44;
    const width = pdf.internal.pageSize.getWidth() - margin * 2;
    let y = 52;
    pdf.setFontSize(15);
    pdf.text(analysisTitle, margin, y);
    y += 26;
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(analysis, width);
    for (const line of lines) {
      if (y > 785) { pdf.addPage(); y = 52; }
      pdf.text(line, margin, y);
      y += 14;
    }
    pdf.save(`participacao-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (loading) return <div className="p-6 text-sm text-ink-subtitle">Carregando participação...</div>;

  return (
    <div className="max-w-[1480px] mx-auto px-4 sm:px-6 py-5 space-y-5">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark">Participação</h2>
          <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Preparação documental, análise e operação dos processos validados.</p>
        </div>
        <label className="block w-full xl:max-w-2xl text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark">
          Processo ativo
          <select aria-label="Processo ativo" value={selectedTenderId} onChange={(event) => setSelectedTenderId(event.target.value)} className="input mt-1">
            <option value="">Selecione uma licitação</option>
            {tenders.map((tender) => <option key={tender.id} value={tender.id}>{tender.title} · {tender.organ}</option>)}
          </select>
        </label>
      </div>

      {!selectedTender ? (
        <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-10 text-center">
          <Files className="w-8 h-8 mx-auto text-ink-subtitle mb-3" />
          <p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">Nenhum processo disponível para participação</p>
          <p className="mt-1 text-xs text-ink-subtitle">Conclua a qualificação em Licitações em andamento.</p>
        </div>
      ) : (
        <>
          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
            <div className="grid xl:grid-cols-[minmax(0,1fr)_410px]">
              <div className="p-4 sm:p-5 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold uppercase text-brand-700 dark:text-brand-300">{PRIORITY_LABELS[selectedTender.priority || 'NONE']}</span>
                  <span className="text-[11px] text-ink-subtitle">{selectedTender.portal || selectedTender.state || 'Portal não informado'}</span>
                </div>
                <h3 className="text-sm sm:text-base font-semibold leading-6 text-ink-title dark:text-ink-title-dark">{selectedTender.title}</h3>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                  <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" />{selectedTender.organ}</span>
                  <span className="flex items-center gap-1.5"><CalendarClock className="w-4 h-4" />{formatDateTime(selectedTender.deadline)}</span>
                  <span className="flex items-center gap-1.5"><FileCheck2 className="w-4 h-4" />{formatMoney(selectedTender.value)}</span>
                </div>
              </div>
              <div className="border-t xl:border-t-0 xl:border-l border-line dark:border-line-dark p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span className="font-medium text-ink-title dark:text-ink-title-dark">Preparação</span>
                  <span className="text-ink-subtitle">{preparationReady} de {preparationItems.length} concluídos</span>
                </div>
                <div className="mt-3 grid grid-cols-5 gap-2" aria-label={`Preparação: ${preparationReady} de ${preparationItems.length} concluídos`}>
                  {preparationItems.map((item) => (
                    <div key={item.label} className="min-w-0 text-center">
                      {item.ready
                        ? <CheckCircle2 className="w-4 h-4 mx-auto text-emerald-600 dark:text-emerald-400" />
                        : <Circle className="w-4 h-4 mx-auto text-ink-subtitle/60" />}
                      <span className="block mt-1 text-[10px] leading-3 text-ink-subtitle break-words">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/png,image/jpeg" className="hidden" onChange={uploadAttachment} />

          <div role="tablist" aria-label="Etapas da participação" className="grid grid-cols-3 border-b border-line dark:border-line-dark">
            {TABS.map((tab, index) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`min-w-0 h-12 px-2 sm:px-4 border-b-2 flex items-center justify-center gap-2 text-[11px] sm:text-xs font-medium transition-colors ${active ? 'border-brand-600 text-brand-700 dark:text-brand-300' : 'border-transparent text-ink-subtitle hover:text-ink-title dark:hover:text-ink-title-dark'}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span className="hidden sm:inline text-[10px] tabular-nums">0{index + 1}</span>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="sm:hidden">{tab.mobileLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {activeTab === 'FILES' && (
            <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-line dark:border-line-dark">
                <div>
                  <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Arquivos do processo</h3>
                  <p className="text-xs text-ink-subtitle mt-0.5">{attachments.length} arquivo(s) vinculado(s)</p>
                </div>
                <button type="button" className="btn-secondary flex items-center justify-center gap-2" disabled={working} onClick={() => fileInputRef.current?.click()}>
                  {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Adicionar arquivo
                </button>
              </div>

              {attachments.length === 0 ? (
                <div className="px-5 py-12 text-center">
                  <Paperclip className="w-7 h-7 mx-auto text-ink-subtitle mb-3" />
                  <p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">Nenhum arquivo vinculado</p>
                  <p className="mt-1 text-xs text-ink-subtitle">Anexe o edital e os documentos necessários para este processo.</p>
                </div>
              ) : (
                <div className="divide-y divide-line dark:divide-line-dark max-h-[460px] overflow-auto">
                  {attachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-4 h-4 flex-shrink-0 text-brand-600" />
                        <span className="text-sm text-ink-text dark:text-ink-text-dark truncate">{attachment.name}</span>
                      </div>
                      {attachment.url && (
                        <button type="button" className="text-xs font-medium text-brand-700 dark:text-brand-300 hover:underline" onClick={() => void openAttachment(attachment)}>Abrir</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt">
                <div className="min-w-0 text-xs">
                  <span className="block font-medium text-ink-title dark:text-ink-title-dark">Documento para análise</span>
                  <span className={`block mt-0.5 truncate ${selectedPdf ? 'text-emerald-700 dark:text-emerald-300' : 'text-ink-subtitle'}`}>{selectedPdf?.name || 'Selecione um PDF ao adicionar o arquivo.'}</span>
                </div>
                <button type="button" className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap" onClick={() => setActiveTab('ANALYSIS')}>
                  Abrir assistente<ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 'ANALYSIS' && (
            <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
              {!selectedPdf && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
                  <span className="text-xs font-medium text-amber-900 dark:text-amber-100">Selecione um PDF do edital para gerar conteúdo.</span>
                  <button type="button" className="btn-secondary w-full sm:w-auto flex items-center justify-center gap-2 whitespace-nowrap" disabled={working} onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4" />Adicionar PDF</button>
                </div>
              )}

              <div className="grid lg:grid-cols-[280px_minmax(0,1fr)]">
                <aside className="p-4 sm:p-5 border-b lg:border-b-0 lg:border-r border-line dark:border-line-dark">
                  <div className="flex items-center gap-2 mb-4">
                    <Bot className="w-4 h-4 text-brand-600" />
                    <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Assistente</h3>
                  </div>
                  {(['ANALYSIS', 'PREPARATION'] as const).map((group) => (
                    <div key={group} className="mb-5 last:mb-0">
                      <p className="mb-2 text-[10px] font-semibold uppercase text-ink-subtitle">{group === 'ANALYSIS' ? 'Analisar edital' : 'Preparar participação'}</p>
                      <div className="space-y-2">
                        {ACTIONS.filter((action) => action.group === group).map((action) => (
                          <button
                            key={action.key}
                            type="button"
                            className={`w-full min-h-10 px-3 py-2 rounded-md border flex items-center gap-2 text-left text-xs font-medium transition-colors ${activeAction === action.key ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950/30 dark:text-brand-300' : 'border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark hover:border-brand-400'}`}
                            disabled={working}
                            onClick={() => runAction(action)}
                          >
                            {working && activeAction === action.key ? <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" /> : <ShieldCheck className="w-4 h-4 flex-shrink-0" />}
                            <span>{action.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </aside>

                <div className="min-w-0 p-4 sm:p-5">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <label htmlFor="participation-analysis-title" className="text-xs font-medium text-ink-title dark:text-ink-title-dark">Documento gerado</label>
                    <button type="button" className="icon-btn" title="Exportar documento em PDF" onClick={exportPdf} disabled={!analysis}><Download className="w-4 h-4" /></button>
                  </div>
                  <input id="participation-analysis-title" value={analysisTitle} onChange={(event) => setAnalysisTitle(event.target.value)} className="input mb-3 font-medium" aria-label="Título do documento" />
                  <textarea value={analysis} onChange={(event) => setAnalysis(event.target.value)} placeholder="O conteúdo gerado ficará disponível aqui para revisão." className="input min-h-[460px] resize-y text-sm leading-6" />
                </div>
              </div>

              <div className="flex justify-end px-4 sm:px-5 py-4 border-t border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt">
                <button type="button" className="btn-primary flex items-center justify-center gap-2" onClick={() => setActiveTab('AUCTION')}>Ir para sala de pregão<ChevronRight className="w-4 h-4" /></button>
              </div>
            </section>
          )}

          {activeTab === 'AUCTION' && <AuctionRoom tender={selectedTender} attachments={attachments} />}
        </>
      )}
    </div>
  );
};

export default Participation;
